import { useEffect, useState } from "react";
import { api, OrgMember } from "../../lib/api";

interface TeamData {
  org: { orgId: string; name: string } | null;
  members: OrgMember[];
  isMember: boolean;
  isOwner?: boolean;
}

export default function SettingsTeam() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [team, setTeam] = useState<TeamData | null>(null);
  // Team access + seat count come from the current plan's catalog capabilities.
  const [canUseTeams, setCanUseTeams] = useState(false);
  const [seatLimit, setSeatLimit] = useState(0);

  // Create org
  const [orgNameInput, setOrgNameInput] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Invite
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [generatedInviteUrl, setGeneratedInviteUrl] = useState<string | null>(
    null,
  );
  const [copiedLink, setCopiedLink] = useState(false);

  // Remove member
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.team.get(), api.billing.plans()])
      .then(([teamData, billingData]) => {
        setTeam(teamData);
        const def = billingData.plans.find(
          (p) => p.id === billingData.current.plan,
        );
        setCanUseTeams(def?.capabilities.team ?? false);
        setSeatLimit(def?.teamSeats ?? 0);
      })
      .catch(() => setError("Failed to load team data"))
      .finally(() => setLoading(false));
  }, []);

  const handleCreateOrg = async () => {
    setCreatingOrg(true);
    setCreateError(null);
    try {
      const { org } = await api.team.createOrg(
        orgNameInput.trim() || undefined,
      );
      setTeam((prev) =>
        prev ? { ...prev, org, members: [], isOwner: true } : prev,
      );
      setOrgNameInput("");
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Failed to create organization",
      );
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteError(null);
    setGeneratedInviteUrl(null);
    setCopiedLink(false);
    try {
      const { inviteUrl } = await api.team.invite(inviteEmail.trim());
      setGeneratedInviteUrl(inviteUrl);
      setInviteEmail("");
      // Reload team to show updated member list
      const updated = await api.team.get();
      setTeam(updated);
    } catch (err) {
      setInviteError(
        err instanceof Error ? err.message : "Failed to send invite",
      );
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (email: string) => {
    if (
      !window.confirm(
        `Remove ${email} from your organization? They will immediately lose access to your workspace.`,
      )
    )
      return;
    setRemovingEmail(email);
    try {
      await api.team.removeMember(email);
      setTeam((prev) =>
        prev
          ? {
              ...prev,
              members: prev.members.filter((m) => m.email !== email),
            }
          : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove member");
    } finally {
      setRemovingEmail(null);
    }
  };

  const handleCopyLink = async () => {
    if (!generatedInviteUrl) return;
    try {
      await navigator.clipboard.writeText(generatedInviteUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    } catch {
      // fallback: select text
    }
  };

  const activeSeats =
    1 +
    (team?.members.filter(
      (m) => m.status === "pending" || m.status === "active",
    ).length ?? 0);
  const seatsRemaining =
    seatLimit === Infinity ? Infinity : seatLimit - activeSeats;

  // ─── Loading / error ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="max-w-3xl">
        <div className="mb-8">
          <h1 className="page-title">Team</h1>
        </div>
        <div className="text-sm text-muted">Loading…</div>
      </div>
    );
  }

  // ─── Member view ────────────────────────────────────────────────────────────

  if (team?.isMember) {
    return (
      <div className="max-w-3xl">
        <div className="mb-8">
          <h1 className="page-title">Team</h1>
          <p className="text-sm text-muted mt-1">
            Your organization membership.
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 p-6">
          <p className="text-sm text-slate">
            You are a member of{" "}
            <span className="font-semibold text-ink">
              {team.org?.name ?? "an organization"}
            </span>
            . Your admin manages invites and team access.
          </p>
        </div>
      </div>
    );
  }

  // ─── Non-qualifying plan ────────────────────────────────────────────────────

  if (!canUseTeams) {
    return (
      <div className="max-w-3xl">
        <div className="mb-8">
          <h1 className="page-title">Team</h1>
          <p className="text-sm text-muted mt-1">
            Invite teammates to collaborate on your workspace.
          </p>
        </div>
        <div className="rounded-xl border border-brand-200 bg-brand-50 p-6">
          <h2 className="section-title mb-1">
            Team features require the Studio plan
          </h2>
          <p className="text-sm text-brand-700 mb-4">
            Upgrade to invite teammates, share brands, and collaborate on
            content.
          </p>
          <a href="/billing" className="btn-primary inline-block text-sm">
            View plans
          </a>
        </div>
      </div>
    );
  }

  // ─── Owner: no org yet ──────────────────────────────────────────────────────

  if (!team?.org) {
    return (
      <div className="max-w-3xl">
        <div className="mb-8">
          <h1 className="page-title">Team</h1>
          <p className="text-sm text-muted mt-1">
            Create an organization to invite teammates.
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="rounded-xl border border-gray-200 p-6">
          <h2 className="section-title mb-4">Create your organization</h2>
          {createError && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {createError}
            </div>
          )}
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Organization name (optional)"
              value={orgNameInput}
              onChange={(e) => setOrgNameInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateOrg()}
              className="input flex-1"
            />
            <button
              onClick={handleCreateOrg}
              disabled={creatingOrg}
              className="btn-primary whitespace-nowrap"
            >
              {creatingOrg ? "Creating…" : "Create organization"}
            </button>
          </div>
          <p className="text-xs text-faint mt-2">
            You can rename your organization later.
          </p>
        </div>
      </div>
    );
  }

  // ─── Owner: org exists ──────────────────────────────────────────────────────

  const activeMembers = team.members.filter((m) => m.status !== "removed");

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <h1 className="page-title">Team</h1>
        <p className="text-sm text-muted mt-1">
          Manage your organization and invite teammates.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Org info */}
      <div className="rounded-xl border border-gray-200 p-5 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="eyebrow mb-0.5">Organization</p>
            <p className="text-base font-semibold text-ink">{team.org.name}</p>
          </div>
          <span className="inline-flex items-center rounded-full bg-gray-100 text-slate text-xs font-medium px-3 py-1">
            {seatLimit === Infinity
              ? "Unlimited seats"
              : `${activeSeats} / ${seatLimit} seats used`}
          </span>
        </div>
      </div>

      {/* Invite form */}
      <div className="rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="section-title mb-3">Invite a team member</h2>

        {inviteError && (
          <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {inviteError}
          </div>
        )}

        {seatsRemaining <= 0 ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            You've used all {seatLimit} seat{seatLimit !== 1 ? "s" : ""} on your
            plan. Remove a member or{" "}
            <a href="/billing" className="underline font-medium">
              upgrade
            </a>{" "}
            to invite more.
          </p>
        ) : (
          <form onSubmit={handleInvite} className="flex gap-3">
            <input
              type="email"
              placeholder="teammate@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="input flex-1"
              required
            />
            <button
              type="submit"
              disabled={inviting}
              className="btn-primary whitespace-nowrap"
            >
              {inviting ? "Inviting…" : "Send invite"}
            </button>
          </form>
        )}

        {/* Generated invite link */}
        {generatedInviteUrl && (
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3">
            <p className="text-xs font-medium text-green-800 mb-2">
              Invite link generated — share this with your teammate:
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={generatedInviteUrl}
                className="input flex-1 text-xs bg-white"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopyLink}
                className="shrink-0 rounded-lg border border-green-300 bg-white px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-50 transition-colors"
              >
                {copiedLink ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Member list */}
      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="section-title">Members</h2>
        </div>

        {activeMembers.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-faint">
            No members yet. Invite someone above.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {activeMembers.map((member) => (
              <li
                key={member.email}
                className="flex items-center justify-between px-5 py-4"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink truncate">
                    {member.email}
                  </p>
                  <p className="text-xs text-faint mt-0.5">
                    {member.status === "active" ? (
                      <>
                        Joined{" "}
                        {member.joinedAt
                          ? new Date(member.joinedAt).toLocaleDateString()
                          : "—"}
                      </>
                    ) : (
                      <>
                        Invite pending ·{" "}
                        {new Date(member.invitedAt).toLocaleDateString()}
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 ml-4 shrink-0">
                  <span
                    className={`inline-flex items-center rounded-full text-xs font-medium px-2.5 py-0.5 ${
                      member.status === "active"
                        ? "bg-green-100 text-green-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {member.status === "active" ? "Active" : "Pending"}
                  </span>
                  <button
                    onClick={() => handleRemove(member.email)}
                    disabled={removingEmail === member.email}
                    className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors disabled:opacity-50"
                  >
                    {removingEmail === member.email ? "Removing…" : "Remove"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
